import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Anchor, Center, Group, Loader, SegmentedControl, Select, Stack, Table, Text, Tooltip } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ApiError, costsApi, overviewApi, type CostOverviewRow, type CostStage, undoPaths } from '../../api';
import { COST_STAGES, formatAmount, formatPercent } from '../../format';
import { AmountCell } from '../../ui/AmountCell';
import { PageHeader, Section } from '../../ui/Section';
import { guardedStep, useUndo } from '../../ui/undo';
import { linkTo } from '../../ui/links';

/**
 * The cost planning of all projects one can see, one line each, in the
 * primary variant: the stages side by side, what is ordered, and how that
 * compares with the reference stage. Risk, reference stage and primary
 * variant can be changed here; they are the same settings as in the project.
 */
export function CostsOverviewPage(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const undo = useUndo();
  const me = queryClient.getQueryData<{ permissions: string[] }>(['me']);
  const canEdit = me?.permissions.includes('costs.write') ?? false;
  const data = useQuery({ queryKey: ['costs-overview'], queryFn: overviewApi.costs });
  const [withRisk, setWithRisk] = useState(true);
  const [gross, setGross] = useState(false);
  const refresh = async (projectId: string): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['costs-overview'] });
    await queryClient.invalidateQueries({ queryKey: ['costs', projectId] });
  };
  const setRisk = useMutation({
    mutationFn: ({ row, riskPercent }: { row: CostOverviewRow; riskPercent: number }) => {
      const variant = row.primaryVariantId;
      const write = variant ? costsApi.updateVariant(row.projectId, variant, { riskPercent }) : costsApi.updatePlan(row.projectId, { riskPercent });
      const path = variant ? undoPaths.costVariant(row.projectId, variant) : undoPaths.costPlan(row.projectId);
      return undo.recordAfter(write, guardedStep(`Risiko ${row.number}`, 'PATCH', path, { riskPercent: row.riskPercent }, { riskPercent }));
    },
    onSettled: (_r, _e, v) => refresh(v.row.projectId),
  });
  const setReference = useMutation({
    mutationFn: ({ row, stage }: { row: CostOverviewRow; stage: CostStage }) => {
      const step = guardedStep(`Bezugsstufe ${row.number}`, 'PATCH', undoPaths.costPlan(row.projectId), { referenceStage: row.referenceStage }, { referenceStage: stage });
      return undo.recordAfter(costsApi.updatePlan(row.projectId, { referenceStage: stage }), step);
    },
    onSettled: (_r, _e, v) => refresh(v.row.projectId),
  });
  const setPrimary = useMutation({
    mutationFn: ({ row, variantId }: { row: CostOverviewRow; variantId: string }) => {
      const back = row.primaryVariantId;
      // Back to the old main variant only while it is still not the main one again.
      const step = back ? guardedStep(`Hauptvariante ${row.number}`, 'PATCH', undoPaths.costVariant(row.projectId, back), { isPrimary: true }, { isPrimary: false }) : null;
      return undo.recordAfter(costsApi.updateVariant(row.projectId, variantId, { isPrimary: true }), step);
    },
    onSettled: (_r, _e, v) => refresh(v.row.projectId),
  });
  const error = setRisk.error ?? setReference.error ?? setPrimary.error;

  if (data.isPending) return <Center h={200}><Loader /></Center>;
  if (data.isError) return <Alert color="red">Die Kostenübersicht konnte nicht geladen werden.</Alert>;
  const rows = data.data;
  const value = (r: CostOverviewRow, s: CostStage): number | null => {
    const v = withRisk ? r.stages[s].withRisk : r.stages[s].net;
    return v === null ? null : gross ? Math.round(v * (1 + r.vatRate / 100) * 100) / 100 : v;
  };
  const sum = (s: CostStage): number | null => {
    const list = rows.map((r) => value(r, s)).filter((v): v is number => v !== null);
    return list.length ? list.reduce((a, b) => a + b, 0) : null;
  };

  return (
    <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
      <Stack gap="md" pb="xl">
        <PageHeader title="Kostenvergleich – alle Projekte" description="Die Kostenplanung aller Projekte in der jeweiligen Hauptvariante. Risiko, Bezugsstufe und Hauptvariante lassen sich hier ändern – es sind dieselben Einstellungen wie im Projekt." />
        {error && <Alert color="red" withCloseButton onClose={() => [setRisk, setReference, setPrimary].forEach((m) => m.reset())}>{error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen.'}</Alert>}
        <Section
          title="Übersicht"
          description={`Beträge ${withRisk ? 'mit Risikozuschlag' : 'ohne Risikozuschlag'}, ${gross ? 'brutto' : 'netto'}, in ganzen Euro. Beauftragt immer netto.`}
          actions={
            <>
              <SegmentedControl size="xs" value={withRisk ? 'risk' : 'plain'} onChange={(v) => setWithRisk(v === 'risk')} data={[{ value: 'risk', label: 'mit Risiko' }, { value: 'plain', label: 'ohne' }]} />
              <SegmentedControl size="xs" value={gross ? 'gross' : 'net'} onChange={(v) => setGross(v === 'gross')} data={[{ value: 'net', label: 'netto' }, { value: 'gross', label: 'brutto' }]} />
            </>
          }
        >
          <Table.ScrollContainer minWidth={1300}>
            <Table verticalSpacing={4} className="pst-tabular" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Projekt</Table.Th>
                  <Table.Th w={170}>Hauptvariante</Table.Th>
                  <Table.Th w={80} ta="right">Risiko (%)</Table.Th>
                  {COST_STAGES.map((s) => <Table.Th key={s.key} w={120} ta="right" title={s.label}>{s.short} (€)</Table.Th>)}
                  <Table.Th w={120} ta="right">Beauftragt (€)</Table.Th>
                  <Table.Th w={150}>Bezugsstufe</Table.Th>
                  <Table.Th w={150}>Kontrolle</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((r) => {
                  const ref = r.stages[r.referenceStage].net;
                  return (
                    <Table.Tr key={r.projectId}>
                      <Table.Td>
                        <Anchor size="sm" fw={600} {...linkTo(navigate, `/projekte/${r.projectId}/kosten`)}>{r.number}</Anchor>
                        <Text size="xs" c="dimmed" lineClamp={1} title={r.name}>{r.name}</Text>
                      </Table.Td>
                      <Table.Td>
                        {r.variants.length > 1 ? (
                          <Select size="xs" allowDeselect={false} disabled={!canEdit} value={r.primaryVariantId} onChange={(v) => v && v !== r.primaryVariantId && setPrimary.mutate({ row: r, variantId: v })} data={r.variants.map((v) => ({ value: v.id, label: `${v.code} ${v.name}` }))}
                            title={r.variants.find((v) => v.id === r.primaryVariantId)?.name} comboboxProps={{ withinPortal: true, width: 'max-content', position: 'bottom-start' }} maxDropdownHeight={320} />
                        ) : (
                          <Text size="sm" c="dimmed">{r.variants[0] ? `${r.variants[0].code} ${r.variants[0].name}` : r.planned ? 'keine Varianten' : 'keine Kostenplanung'}</Text>
                        )}
                      </Table.Td>
                      <Table.Td><AmountCell value={r.riskPercent} digits={0} readOnly={!canEdit || !r.planned} onCommit={(v) => setRisk.mutate({ row: r, riskPercent: Math.min(100, Math.max(0, v ?? 0)) })} /></Table.Td>
                      {COST_STAGES.map((s) => (
                        <Table.Td key={s.key} ta="right" style={s.key === r.referenceStage ? { fontWeight: 700 } : undefined}>
                          <Text size="sm" c={value(r, s.key) === null ? 'dimmed' : undefined} fw={s.key === r.referenceStage ? 700 : undefined}>{value(r, s.key) === null ? '—' : formatAmount(value(r, s.key), 0)}</Text>
                        </Table.Td>
                      ))}
                      <Table.Td ta="right"><Text size="sm" c={r.committed ? undefined : 'dimmed'}>{r.committed ? formatAmount(r.committed, 0) : '—'}</Text></Table.Td>
                      <Table.Td>
                        <Select size="xs" allowDeselect={false} disabled={!canEdit || !r.planned} value={r.referenceStage} onChange={(v) => v && v !== r.referenceStage && setReference.mutate({ row: r, stage: v as CostStage })} data={COST_STAGES.map((s) => ({ value: s.key, label: s.label }))} />
                      </Table.Td>
                      <Table.Td><Control committed={r.committed} planned={ref} /></Table.Td>
                    </Table.Tr>
                  );
                })}
                {rows.length > 1 && (
                  <Table.Tr style={{ borderTop: '2px solid var(--mantine-color-default-border)' }}>
                    <Table.Td><Text size="sm" fw={700}>Summe</Text></Table.Td>
                    <Table.Td /><Table.Td />
                    {COST_STAGES.map((s) => <Table.Td key={s.key} ta="right"><Text size="sm" fw={700}>{sum(s.key) === null ? '—' : formatAmount(sum(s.key), 0)}</Text></Table.Td>)}
                    <Table.Td ta="right"><Text size="sm" fw={700}>{formatAmount(rows.reduce((a, r) => a + r.committed, 0), 0)}</Text></Table.Td>
                    <Table.Td /><Table.Td />
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Section>
      </Stack>
    </div>
  );
}

/** Ordered against the reference stage (net), as in the project's cost control. */
function Control({ committed, planned }: { committed: number; planned: number | null }): JSX.Element | null {
  if (!planned || committed === 0) return null;
  const share = committed / planned;
  const over = share > 1;
  return (
    <Tooltip label={`${formatAmount(committed, 0)} € beauftragt von ${formatAmount(planned, 0)} € geplant (netto)`}>
      <Group gap={6} wrap="nowrap">
        <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'var(--mantine-color-default-border)', overflow: 'hidden' }}>
          <div style={{ width: `${Math.min(share, 1) * 100}%`, height: '100%', borderRadius: 4, background: over ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-brand-6)' }} />
        </div>
        <Text size="xs" c={over ? 'red.7' : 'dimmed'} style={{ whiteSpace: 'nowrap' }}>{over ? `+${formatPercent((share - 1) * 100)} über` : formatPercent(share * 100)}</Text>
      </Group>
    </Tooltip>
  );
}
